import { AnalyticsEvent, EventCategory, trackUnifiedEvent } from 'services/analytics'
import { canonicalLoadboardAnalyticsName } from '@numeo/types-core'
import { createLoadHubAnalyticsId, setActiveLoadHubActivation, trackLoadHubEvent } from './analyticsContext'
import { registerLoadboardConnectionAttempt, resolveLoadboardConnectionAttempt } from '../api/loadboards.api'

export {
    changedLoadHubFilterFields,
    createSourceSearchTracker,
    trackFiltersApplied,
    trackExtensionSourceSearch,
    trackSearchBlocked,
    trackSearchSubmitted,
} from './searchAnalytics'
export type { SearchTrigger, SourceSearchTracker } from './searchAnalytics'

type ConnectionAuthFlow = 'credentials' | 'otp' | 'browser_extension'
export type ConnectionOutcome = 'success' | 'failure' | 'cancelled'
type LoadInteractionAction = 'details_opened' | 'call_clicked' | 'external_loadboard_opened' | 'offer_compose_opened' | 'book_compose_opened'
type LoadHubEmailType = 'quick' | 'info' | 'offer' | 'book'
type EmailOutcome = 'sent' | 'deduplicated' | 'failed'

/** Canonical chart key. Never use `source`: the analytics wrappers reserve that property. */
const toCanonicalLoadboard = canonicalLoadboardAnalyticsName

const trackLoadHub = (action: AnalyticsEvent, properties: Record<string, string | number | boolean | string[]> = {}): void => {
    trackLoadHubEvent(action, { surface: 'load_hub', ...properties })
}

const errorText = (error: unknown): string => {
    if (typeof error === 'string') return error.toLowerCase()
    if (error && typeof error === 'object') {
        const response = 'response' in error ? (error as { response?: { status?: number; data?: { message?: string } } }).response : undefined
        const message = error instanceof Error ? error.message : ''
        return `${message} ${response?.status ?? ''} ${response?.data?.message ?? ''}`.toLowerCase()
    }
    return ''
}

const classifyConnectionFailure = (error: unknown): string => {
    const text = errorText(error)
    if (/401|unauthor|invalid.*(credential|password|login)|wrong.*password/.test(text)) return 'invalid_credentials'
    if (/otp|verification code|passcode/.test(text)) return 'otp_invalid'
    if (/extension/.test(text)) return 'extension_unavailable'
    if (/network|fetch|offline|econn/.test(text)) return 'network'
    if (/timeout|timed out/.test(text)) return 'timeout'
    return 'unknown'
}

const connectionErrorProperties = (error: unknown): Record<string, string | number | boolean> => {
    const code = classifyConnectionFailure(error)
    const response = error && typeof error === 'object' && 'response' in error ? (error as { response?: { status?: number } }).response : undefined
    const category =
        code === 'invalid_credentials'
            ? 'authentication'
            : code === 'otp_invalid'
              ? 'verification'
              : code === 'extension_unavailable'
                ? 'extension'
                : code === 'network' || code === 'timeout'
                  ? 'network'
                  : 'unknown'
    return {
        error_id: createLoadHubAnalyticsId('err'),
        error_category: category,
        error_code: code === 'unknown' ? 'unmapped_error' : code,
        is_retriable: true,
        ...(typeof response?.status === 'number' ? { http_status: response.status } : {}),
        user_visible: true,
    }
}

export interface ConnectionAttempt {
    startedAt: number
    attemptId: string
    connectionFlowId?: string
    connectionMethod: string
    /** Resolves after the durable server-side attempt record exists. */
    registrationPromise: Promise<void>
    recordStep: (
        step: string,
        phase: 'entered' | 'submitted' | 'succeeded' | 'failed',
        options?: {
            error?: unknown
            stepDurationMs?: number
            otpMethod?: 'sms' | 'email' | 'authenticator_app' | 'none' | 'unknown'
            owner?: 'web' | 'extension'
        }
    ) => void
    complete: (outcome: ConnectionOutcome, options?: { error?: unknown; failureReason?: string; connectionSequence?: number }) => void
}

export const beginConnectionAttempt = (params: {
    board: string
    authFlow: ConnectionAuthFlow
    hasEverConnected?: boolean
    connectionSequence?: number
    analyticsEnabled?: boolean
    connectionFlowId?: string
    methodsOffered?: string[]
    previousAttemptId?: string
    connectionMethod?: 'extension_session_capture' | 'email_password' | 'phone_otp' | 'corporate_user_credentials'
}): ConnectionAttempt => {
    const startedAt = Date.now()
    const attemptId = createLoadHubAnalyticsId('conn_attempt')
    const loadboard = toCanonicalLoadboard(params.board)
    const connectionKind = params.hasEverConnected ? 'reconnect' : 'initial'
    let completed = false
    const connectionMethod =
        params.connectionMethod ??
        (params.authFlow === 'browser_extension' ? 'extension_session_capture' : params.authFlow === 'otp' ? 'phone_otp' : 'email_password')
    const base = {
        loadboard,
        ...(params.connectionFlowId ? { connection_flow_id: params.connectionFlowId } : {}),
        connection_attempt_id: attemptId,
        ...(params.previousAttemptId ? { previous_attempt_id: params.previousAttemptId } : {}),
        connection_kind: connectionKind,
        connection_intent: params.hasEverConnected ? 'reconnect_after_loss' : 'first_connection',
        connection_method: connectionMethod,
        auth_flow: params.authFlow,
        methods_offered: params.methodsOffered ?? [connectionMethod],
        attempt_started_at: new Date(startedAt).toISOString(),
    }
    const registrationPromise =
        params.analyticsEnabled !== false
            ? registerLoadboardConnectionAttempt({
                  loadboard,
                  connectionAttemptId: attemptId,
                  connectionFlowId: params.connectionFlowId,
                  connectionMethod,
                  connectionIntent: params.hasEverConnected ? 'reconnect_after_loss' : 'first_connection',
                  attemptStartedAt: new Date(startedAt).toISOString(),
              }).catch(() => undefined)
            : Promise.resolve()
    if (params.analyticsEnabled !== false) {
        trackLoadHub(AnalyticsEvent.LOAD_HUB_SOURCE_CONNECTION_STARTED, base)
        trackLoadHubEvent(AnalyticsEvent.WEB_LOAD_HUB_CONNECTION_STARTED, base)
    }

    let lastStep = 'method_selection'
    return {
        startedAt,
        attemptId,
        connectionFlowId: params.connectionFlowId,
        connectionMethod,
        registrationPromise,
        recordStep: (step, phase, options = {}) => {
            if (params.analyticsEnabled === false || completed) return
            lastStep = step
            const owner = options.owner ?? 'web'
            trackLoadHubEvent(
                owner === 'extension' ? AnalyticsEvent.EXT_LOAD_HUB_CONNECTION_STEP_RECORDED : AnalyticsEvent.WEB_LOAD_HUB_CONNECTION_STEP_RECORDED,
                {
                    ...base,
                    step,
                    phase,
                    step_visit_id: createLoadHubAnalyticsId('conn_step'),
                    ...(options.stepDurationMs !== undefined ? { step_duration_ms: Math.max(0, options.stepDurationMs) } : {}),
                    ...(options.otpMethod ? { otp_method: options.otpMethod } : {}),
                    ...(phase === 'failed'
                        ? {
                              failure_reason: classifyConnectionFailure(options.error),
                              failure_stage: step,
                              ...connectionErrorProperties(options.error),
                          }
                        : {}),
                },
                {
                    humanActivity: false,
                    ...(owner === 'extension' ? { initiatedFrom: 'extension' as const, platform: 'extension' as const } : {}),
                }
            )
        },
        complete: (outcome, options = {}) => {
            if (completed) return
            completed = true
            if (params.analyticsEnabled === false) return
            const failureReason =
                outcome === 'success'
                    ? undefined
                    : (options.failureReason ?? (outcome === 'cancelled' ? 'user_cancelled' : classifyConnectionFailure(options.error)))
            void registrationPromise.then(() =>
                resolveLoadboardConnectionAttempt(attemptId, {
                    status: outcome === 'success' ? 'succeeded' : outcome === 'failure' ? 'failed' : 'cancelled',
                    lastStep,
                    ...(failureReason ? { errorCode: failureReason } : {}),
                }).catch(() => undefined)
            )
            if (outcome === 'cancelled') {
                trackLoadHubEvent(AnalyticsEvent.WEB_LOAD_HUB_CONNECTION_CANCELLED, {
                    ...base,
                    last_step: lastStep,
                    elapsed_ms: Math.max(0, Date.now() - startedAt),
                    reason: options.failureReason === 'method_switched' ? 'method_switched' : 'user_cancelled',
                })
            }
            trackLoadHub(AnalyticsEvent.LOAD_HUB_SOURCE_CONNECTION_COMPLETED, {
                ...base,
                outcome,
                duration_ms: Math.max(0, Date.now() - startedAt),
                connection_sequence: options.connectionSequence ?? Math.max(0, params.connectionSequence ?? 0) + (outcome === 'success' ? 1 : 0),
                ...(outcome === 'success' ? {} : { failure_reason: failureReason ?? 'unknown' }),
            })
            // Temporary compatibility for daily-stats; remove after the report reads the
            // canonical completed event and production validation is finished.
            if (outcome === 'success') trackLoadHub(AnalyticsEvent.LOAD_HUB_SOURCE_CONNECTED, { loadboard })
        },
    }
}

export const trackLoadHubPageViewed = (): void => trackLoadHub(AnalyticsEvent.LOAD_HUB_PAGE_VIEWED)

type LoadHubEntryPoint = 'sidebar' | 'spot_home' | 'extension_open' | 'extension_more_loads' | 'extension_see_more' | 'direct_link' | 'other' | 'unknown'

export const getLoadHubEntryPoint = (): LoadHubEntryPoint => {
    if (typeof window === 'undefined') return 'unknown'
    const value = new URLSearchParams(window.location.search).get('entry')
    const allowed = new Set<LoadHubEntryPoint>([
        'sidebar',
        'spot_home',
        'extension_open',
        'extension_more_loads',
        'extension_see_more',
        'direct_link',
        'other',
        'unknown',
    ])
    if (value && allowed.has(value as LoadHubEntryPoint)) return value as LoadHubEntryPoint
    return new URLSearchParams(window.location.search).get('source') === 'extension_sidepanel' ? 'extension_open' : 'unknown'
}

export const trackDetailedLoadHubView = (params: {
    userId?: string
    companyId?: string
    selectedSources: string[]
    connectedSources: string[]
    emailConnected: boolean
    extensionState: 'confirmed_available' | 'confirmed_unavailable' | 'unknown'
    autoRefreshEnabled?: boolean
    refreshAverageSeconds?: number
    entryPoint?: LoadHubEntryPoint
}): string => {
    const viewId = createLoadHubAnalyticsId('view')
    const activationKey = params.userId && params.companyId ? `load_hub_activation_v1:${params.companyId}:${params.userId}` : undefined
    let activationId: string | undefined
    let activationFirstViewAt: number | undefined
    let isFirstObservedHubView = false
    if (activationKey) {
        try {
            const stored = localStorage.getItem(activationKey)
            if (stored) {
                try {
                    const parsed = JSON.parse(stored) as { id?: string; firstViewAt?: number }
                    activationId = typeof parsed.id === 'string' ? parsed.id : undefined
                    activationFirstViewAt = typeof parsed.firstViewAt === 'number' ? parsed.firstViewAt : undefined
                } catch {
                    activationId = stored
                }
            }
            if (!activationId) {
                activationId = createLoadHubAnalyticsId('activation')
                activationFirstViewAt = Date.now()
                localStorage.setItem(activationKey, JSON.stringify({ id: activationId, firstViewAt: activationFirstViewAt }))
                isFirstObservedHubView = true
            }
        } catch {
            activationId = undefined
        }
    }
    setActiveLoadHubActivation(activationId, activationFirstViewAt)
    trackLoadHubEvent(AnalyticsEvent.WEB_LOAD_HUB_VIEWED, {
        view_id: viewId,
        ...(activationId ? { activation_id: activationId } : {}),
        entry_point: params.entryPoint ?? 'unknown',
        selected_sources: [...new Set(params.selectedSources.map(toCanonicalLoadboard))],
        connected_sources: [...new Set(params.connectedSources.map(toCanonicalLoadboard))],
        connection_state_observed_at: new Date().toISOString(),
        connection_verification_level: 'stored_state_only',
        email_connected: params.emailConnected,
        extension_state: params.extensionState,
        auto_refresh_enabled: params.autoRefreshEnabled ?? false,
        ...(params.refreshAverageSeconds !== undefined ? { refresh_average_seconds: params.refreshAverageSeconds } : {}),
        is_first_observed_hub_view: isFirstObservedHubView,
        attribution_status: 'not_applicable',
    })
    return viewId
}

export const trackSourcesChanged = (params: {
    previousSources: string[]
    selectedSources: string[]
    changedSource: string
    action: 'selected' | 'deselected'
    connectionStateAtAction: 'connected' | 'disconnected' | 'connecting' | 'unknown' | 'not_applicable'
}): void => {
    trackLoadHubEvent(AnalyticsEvent.WEB_LOAD_HUB_SOURCES_CHANGED, {
        previous_sources: [...new Set(params.previousSources.map(toCanonicalLoadboard))],
        selected_sources: [...new Set(params.selectedSources.map(toCanonicalLoadboard))],
        changed_source: toCanonicalLoadboard(params.changedSource),
        action: params.action,
        connection_state_at_action: params.connectionStateAtAction,
    })
}

export const trackResultsSortChanged = (
    sortField: 'age' | 'rate' | 'rpm' | 'origin_deadhead' | 'destination_deadhead',
    sortDirection: 'asc' | 'desc',
    context?: { searchId?: string; searchExecutionId?: string }
): void => {
    trackLoadHubEvent(AnalyticsEvent.WEB_LOAD_HUB_RESULTS_SORT_CHANGED, {
        sort_field: sortField,
        sort_direction: sortDirection,
        ...(context?.searchId ? { search_id: context.searchId } : {}),
        ...(context?.searchExecutionId ? { search_execution_id: context.searchExecutionId } : {}),
    })
}

export const trackLoadHubUiAction = (
    action:
        | 'search_tab_created'
        | 'search_tab_closed'
        | 'recent_search_selected'
        | 'hide_selected'
        | 'hide_duplicates_changed'
        | 'call_provider_changed'
        | 'detail_tool_opened',
    properties: Record<string, string | number | boolean | string[]> = {}
): void => trackLoadHubEvent(AnalyticsEvent.WEB_LOAD_HUB_UI_ACTION, { action, ...properties })

export const trackConnectionDialogOpened = (params: {
    board: string
    methodsOffered: string[]
    newAuthEnabled: boolean
    priorConnectionState: 'connected' | 'disconnected' | 'connecting' | 'unknown' | 'not_applicable'
}): string => {
    const flowId = createLoadHubAnalyticsId('conn_flow')
    trackLoadHubEvent(AnalyticsEvent.WEB_LOAD_HUB_CONNECTION_DIALOG_OPENED, {
        connection_flow_id: flowId,
        loadboard: toCanonicalLoadboard(params.board),
        methods_offered: params.methodsOffered,
        load_hub_new_auth_enabled: params.newAuthEnabled,
        prior_connection_state: params.priorConnectionState,
    })
    return flowId
}

export const trackConnectionMethodSelected = (params: {
    board: string
    connectionFlowId: string
    connectionMethod: string
    methodsOffered: string[]
    selectionOrigin: 'user' | 'only_available_method'
}): void => {
    trackLoadHubEvent(AnalyticsEvent.WEB_LOAD_HUB_CONNECTION_METHOD_SELECTED, {
        connection_flow_id: params.connectionFlowId,
        loadboard: toCanonicalLoadboard(params.board),
        connection_method: params.connectionMethod,
        methods_offered: params.methodsOffered,
        selection_origin: params.selectionOrigin,
    })
}

/** @deprecated Daily-stats compatibility only; new connection flows use beginConnectionAttempt. */
export const trackSourceConnected = (board: string | null | undefined): void => {
    if (!board) return
    trackUnifiedEvent({
        category: EventCategory.FEATURE_USE,
        action: AnalyticsEvent.LOAD_HUB_SOURCE_CONNECTED,
        loadboard: toCanonicalLoadboard(board),
    })
}

export const trackSourceErrorShown = (board: string, errorCode: string, action: string): void => {
    trackLoadHub(AnalyticsEvent.LOAD_HUB_SOURCE_ERROR_SHOWN, { loadboard: toCanonicalLoadboard(board), error_code: errorCode, action })
}

export const trackSourceErrorActionClicked = (board: string, errorCode: string, action: string): void => {
    trackLoadHub(AnalyticsEvent.LOAD_HUB_SOURCE_ERROR_ACTION_CLICKED, { loadboard: toCanonicalLoadboard(board), error_code: errorCode, action })
}

export const trackLoadInteraction = (board: string | null | undefined, action: LoadInteractionAction): void => {
    if (!board) return
    trackLoadHub(AnalyticsEvent.LOAD_HUB_LOAD_INTERACTION, { loadboard: toCanonicalLoadboard(board), action })
}

const classifyEmailFailure = (error: unknown): string => {
    const text = errorText(error)
    if (/401|unauth|reauth|expired.*(grant|connection)/.test(text)) return 'email_auth_required'
    if (/429|rate.?limit/.test(text)) return 'rate_limited'
    if (/400|invalid|recipient|validation/.test(text)) return 'validation'
    if (/network|fetch|offline|econn|timeout/.test(text)) return 'network'
    return 'unknown'
}

export const trackEmailAttempted = (board: string | null | undefined, emailType: LoadHubEmailType): void => {
    if (!board) return
    trackLoadHub(AnalyticsEvent.LOAD_HUB_EMAIL_ATTEMPTED, { loadboard: toCanonicalLoadboard(board), email_type: emailType })
}

interface EmailCompletionOptions {
    error?: unknown
    /** Numeric compatibility field used by the legacy offer/book daily report. */
    legacyRate?: number
}

export const trackEmailCompleted = (
    board: string | null | undefined,
    emailType: LoadHubEmailType,
    outcome: EmailOutcome,
    options: EmailCompletionOptions = {}
): void => {
    if (!board) return
    trackLoadHub(AnalyticsEvent.LOAD_HUB_EMAIL_COMPLETED, {
        loadboard: toCanonicalLoadboard(board),
        email_type: emailType,
        outcome,
        ...(outcome === 'failed' ? { failure_reason: classifyEmailFailure(options.error) } : {}),
    })
    // Temporary daily-stats compatibility. Keep only bounded dimensions; never include
    // broker/sender addresses, lane strings, or raw provider errors.
    if (outcome !== 'deduplicated') {
        const legacyEvent =
            emailType === 'book'
                ? outcome === 'sent'
                    ? AnalyticsEvent.LOAD_HUB_BOOK_EMAIL_SENT
                    : AnalyticsEvent.LOAD_HUB_BOOK_EMAIL_FAILED
                : emailType === 'offer'
                  ? outcome === 'sent'
                      ? AnalyticsEvent.LOAD_HUB_OFFER_EMAIL_SENT
                      : AnalyticsEvent.LOAD_HUB_OFFER_EMAIL_FAILED
                  : emailType === 'info'
                    ? outcome === 'sent'
                        ? AnalyticsEvent.LOAD_HUB_INFO_EMAIL_SENT
                        : AnalyticsEvent.LOAD_HUB_INFO_EMAIL_FAILED
                    : outcome === 'sent'
                      ? AnalyticsEvent.LOAD_HUB_EMAIL_SENT
                      : AnalyticsEvent.LOAD_HUB_EMAIL_FAILED
        trackLoadHub(legacyEvent, {
            loadboard: toCanonicalLoadboard(board),
            email_type: emailType,
            ...(options.legacyRate !== undefined ? { rate: options.legacyRate } : {}),
        })
    }
}
